/**
 * Попереднє завантаження коду в простої (UX-33; performance-budget.md, розд. 3 і 3.2).
 *
 * Після першого екрана (подія `load`, простій і межа першого екрана — `perf/first-screen.ts`) по
 * черзі вантажимо ліниві чанки, які людина найімовірніше відкриє наступними: палітру, потім код
 * маршрутів. Межа потрібна, бо простій після `load` настає ще до даних стартового екрана: чанки
 * змагалися б із ними за мережу, а мітка `prefetch:start` лягала б раніше за сам екран. Лише КОД,
 * без даних: запитів до API тут немає. Межі в замірі — мітки `prefetch:start` (кінець «запитів першого
 * екрана») і `prefetch:done` (усе завантажено): між ними — окрема метрика «попереднє
 * завантаження». Легкий режим і «Економія трафіку» нічого не вантажать (lite-mode.md, розд. 1),
 * міток тоді немає. Прихована (фонова) вкладка чекає, доки її покажуть (speed.md, UX-33).
 *
 * Чекаємо саме `load`: у Firefox `requestIdleCallback` може спрацювати раніше, і `<link>` лінивих
 * чанків затягнув би `load` сторінки.
 */
import { isFirstScreenDone, onFirstScreenBoundary } from './first-screen.ts';

export type PrefetchTask = () => Promise<unknown> | undefined;

export const PREFETCH_MARKS = { start: 'prefetch:start', done: 'prefetch:done' } as const;

/**
 * Код екрана «після першого екрана» (E4a-W2: сортування в шапці, вибір стовпців, живе оновлення
 * списку): виконується, щойно планувальник поставив `prefetch:start`, а не власним простоєм.
 * Свій `requestIdleCallback` екрана спрацював би раніше за планувальник (ефекти дочірніх
 * компонентів ідуть першими), і чанк став би «запитом першого екрана» (performance-budget.md,
 * розд. 3). Легкий режим і «Економія трафіку» планувальника не запускають: тоді екран вантажить
 * код на намір людини (наведення, фокус) або за власним таймером.
 */
let lateStarted = false;
const lateTasks = new Set<() => void>();

export function afterPrefetchStart(task: () => void): () => void {
  if (lateStarted) {
    task();
    return () => undefined;
  }
  lateTasks.add(task);
  return () => lateTasks.delete(task);
}

function startLateTasks() {
  lateStarted = true;
  const tasks = [...lateTasks];
  lateTasks.clear();
  for (const task of tasks) task();
}

/** Тести: свіжий стан черги «після першого екрана». */
export function resetLateTasksForTests(started = false) {
  lateStarted = started;
  lateTasks.clear();
}

/** Середовище браузера — підмінне в тестах (jsdom не має `requestIdleCallback`). */
export interface PrefetchEnv {
  /** Документ уже завантажено (`readyState === 'complete'`). */
  loaded(): boolean;
  /** Підписка на `load`; повертає відписку. */
  onLoad(cb: () => void): () => void;
  /** Виклик у простої; повертає скасування. */
  idle(cb: () => void): () => void;
  mark(name: string): void;
  /** Людина просить берегти трафік (`navigator.connection.saveData`). */
  saveData(): boolean;
  /** Вкладка прихована (фонова). */
  hidden(): boolean;
  /** Підписка на «вкладку показали»; повертає відписку. */
  onVisible(cb: () => void): () => void;
  /** Межу першого екрана вже пройдено (`perf/first-screen.ts`). */
  firstScreenDone(): boolean;
  /** На межі першого екрана — раніше за `afterFirstScreen`; повертає скасування. */
  firstScreen(cb: () => void): () => void;
}

interface NetworkInformationLike {
  saveData?: boolean;
}

export const browserEnv: PrefetchEnv = {
  loaded: () => document.readyState === 'complete',
  onLoad: (cb) => {
    window.addEventListener('load', cb, { once: true });
    return () => window.removeEventListener('load', cb);
  },
  idle: (cb) => {
    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(cb);
      return () => window.cancelIdleCallback(id);
    }
    // Safari без `requestIdleCallback`: після `load` даємо сторінці 2 с.
    const id = window.setTimeout(cb, 2000);
    return () => window.clearTimeout(id);
  },
  mark: (name) => performance.mark?.(name),
  saveData: () =>
    Boolean(
      (navigator as Navigator & { connection?: NetworkInformationLike }).connection?.saveData,
    ),
  hidden: () => document.visibilityState === 'hidden',
  onVisible: (cb) => {
    const on = () => {
      if (document.visibilityState === 'visible') {
        document.removeEventListener('visibilitychange', on);
        cb();
      }
    };
    document.addEventListener('visibilitychange', on);
    return () => document.removeEventListener('visibilitychange', on);
  },
  firstScreenDone: isFirstScreenDone,
  firstScreen: onFirstScreenBoundary,
};

/**
 * Запланувати завантаження `tasks` у простої. Завдання йдуть по черзі, у заданому порядку: перше
 * — найважливіше (палітра має встигнути до першого `Ctrl+K`). Збій одного завдання не зупиняє
 * решту: чанк довантажиться на вимогу. Повертає скасування (до старту — нічого не вантажиться).
 */
export function scheduleIdlePrefetch(
  tasks: readonly PrefetchTask[],
  { lite, env = browserEnv }: { lite: boolean; env?: PrefetchEnv },
): () => void {
  if (lite || tasks.length === 0 || env.saveData()) return () => undefined;
  let cancelled = false;
  let cancelIdle: (() => void) | undefined;
  let cancelVisible: (() => void) | undefined;
  let cancelScreen: (() => void) | undefined;
  const run = async () => {
    env.mark(PREFETCH_MARKS.start);
    startLateTasks();
    for (const task of tasks) {
      if (cancelled) return;
      try {
        await task();
      } catch {
        // Мережа чи нова версія збірки: чанк довантажиться, коли знадобиться.
      }
    }
    if (!cancelled) env.mark(PREFETCH_MARKS.done);
  };
  // Фонова вкладка: не забираємо мережу й процесор у видимої, чекаємо показу.
  const start = () => {
    if (env.hidden()) cancelVisible = env.onVisible(schedule);
    else void run();
  };
  const schedule = () => {
    cancelIdle = env.idle(start);
  };
  let unsubscribe: (() => void) | undefined;
  const afterLoad = () => {
    if (env.loaded()) schedule();
    else unsubscribe = env.onLoad(schedule);
  };
  if (env.firstScreenDone()) afterLoad();
  else {
    // Перший екран ще не готовий: стаємо на його межу одразу, а не після `load` і простою. Так
    // мітка `prefetch:start` ляже раніше за відкладене (`afterFirstScreen`), навіть якщо `load`
    // настав пізно. Межа сама настає після простою, тож другого простою не чекаємо.
    cancelScreen = env.firstScreen(() => {
      if (env.loaded()) start();
      else unsubscribe = env.onLoad(schedule);
    });
  }
  return () => {
    cancelled = true;
    unsubscribe?.();
    cancelIdle?.();
    cancelScreen?.();
    cancelVisible?.();
  };
}
